// «Mi aprendizaje» (brief 26-oct-2026, paso 3): el examen diario de 10 preguntas con los productos que la
// vendedora cargó ESE día. Puro: el servidor lo arma con un azar sembrado (mismo examen si se reintenta) y
// guarda la respuesta correcta; el navegador nunca la recibe hasta terminar.
//
//   marca→genérico ×2 · ¿pide receta? ×2 (valen doble) · ¿para qué sirve? ×2 (fichas APROBADAS) ·
//   caso de mostrador ×2 (banco fijo: lo correcto es llamar al químico) · práctica ×2 (precio y stock en 30 s)
//   = 12 puntos, aprueba con 10, 10 minutos.
//
// Si faltan fichas aprobadas para «¿para qué sirve?», esas preguntas pasan a marca→genérico (y se dice).
import { tipoEfectivo } from "../catalogo/buscador";
import { normalizarNombre } from "../catalogo/normalizar";
import { claveDci } from "./clave";

export const EXAMEN_MINUTOS = 10;
export const PRACTICA_SEGUNDOS = 30;
/** Margen para la red entre «abrir» y «responder» una práctica. */
export const PRACTICA_MARGEN_SEGUNDOS = 5;
export const PUNTAJE_MAXIMO = 12;
export const PUNTAJE_APRUEBA = 10;

export type TipoPregunta = "generico" | "receta" | "para_que" | "caso" | "practica";

export type ProductoExamen = {
  producto_id: string;
  nombre: string;
  principio_activo: string | null;
  tipo: string | null; // marca/generico/aseo/galenico marcado por la botica (null = por el nombre)
  requiere_receta: boolean;
  precio_dm: number | null; // precio al público (con IGV) de la presentación base en esta botica
  stock: number | null; // unidades base al armar el examen
};

export type FichaExamen = { clave: string; nombre_mostrar: string; para_que_sirve: string };

export type PreguntaArmada = {
  orden: number;
  tipo: TipoPregunta;
  enunciado: string;
  opciones: string[] | null; // null = se responde escribiendo (práctica)
  correcta: string; // la opción; en la práctica JSON {"precio_dm":…, "stock":…}
  explicacion: string;
  puntos: number;
  producto_id: string | null;
};

export { claveDci };

/** La ficha de un principio activo: igual, o la ficha es el comienzo («amoxicilina» para «amoxicilina trihidrato»). */
export function fichaDe<T extends { clave: string }>(fichas: T[], pa: string | null | undefined): T | null {
  const c = claveDci(pa);
  if (!c) return null;
  let mejor: T | null = null;
  for (const f of fichas) {
    const esCombo = f.clave.includes(" + ");
    if (f.clave === c || (!esCombo && !c.includes(" + ") && c.startsWith(f.clave + " "))) {
      if (!mejor || f.clave.length > mejor.clave.length) mejor = f;
    }
  }
  return mejor;
}

// ---- Banco fijo de casos de mostrador (Kevin, 26-oct: «lo correcto siempre es llamar al químico») ----
export const LLAMAR_QUIMICO = "Llamar al químico farmacéutico antes de vender";
export const CASOS_MOSTRADOR: { enunciado: string; distractores: [string, string, string]; explicacion: string }[] = [
  {
    enunciado: "Una señora pide amoxicilina para su hijo de 3 años que tiene fiebre. No trae receta.",
    distractores: ["Venderle el jarabe de amoxicilina", "Venderle las cápsulas y que las abra", "Recomendarle otro antibiótico más barato"],
    explicacion: "Un antibiótico pide receta y la dosis de un niño la fija el médico. Tú no decides: llamas al químico.",
  },
  {
    enunciado: "Un joven pide «algo fuerte» para un dolor de muela y dice que el ibuprofeno ya no le hace nada.",
    distractores: ["Venderle ketorolaco", "Venderle dos cajas de ibuprofeno", "Recomendarle tomar doble dosis"],
    explicacion: "Un dolor que no cede con lo habitual es señal de alarma. El químico evalúa y deriva.",
  },
  {
    enunciado: "Una clienta embarazada pregunta qué puede tomar para la gripe.",
    distractores: ["Venderle un antigripal de venta libre", "Decirle que tome paracetamol con clorfenamina", "Recomendarle vitamina C y que vuelva mañana"],
    explicacion: "En el embarazo muchos medicamentos están contraindicados. Siempre decide el químico.",
  },
  {
    enunciado: "Un señor mayor trae una bolsa con 6 medicamentos y quiere algo para dormir.",
    distractores: ["Venderle clorfenamina, que da sueño", "Recomendarle un té y venderle melatonina", "Venderle lo que él pida"],
    explicacion: "Con muchos medicamentos hay riesgo de interacciones y caídas. Lo ve el químico.",
  },
  {
    enunciado: "Piden dexametasona en ampolla «para inyectarse en casa por un dolor de espalda». No hay receta.",
    distractores: ["Vendérsela porque es barata", "Venderle diclofenaco inyectable en su lugar", "Explicarle cómo inyectarse"],
    explicacion: "Un corticoide inyectable pide receta y tiene riesgos. Tú no lo vendes: llamas al químico.",
  },
  {
    enunciado: "Una mamá dice que su bebé de 6 meses tiene diarrea desde ayer y pregunta qué darle.",
    distractores: ["Venderle loperamida", "Venderle un antibiótico para la diarrea", "Decirle que espere dos días más"],
    explicacion: "En un bebé la diarrea puede deshidratar rápido. Es una señal de alarma: el químico orienta y deriva.",
  },
  {
    enunciado: "Un cliente dice que la pastilla que le vendieron ayer le dio ronchas en todo el cuerpo.",
    distractores: ["Cambiarle el producto por otra marca", "Venderle loratadina y listo", "Decirle que siga tomándola"],
    explicacion: "Puede ser una alergia al medicamento. Se avisa al químico de inmediato.",
  },
  {
    enunciado: "Te piden tres cajas de un jarabe para la tos «porque se acaba rápido» y te ponen nerviosa las preguntas.",
    distractores: ["Vender las tres cajas", "Vender una y decirle que vuelva", "Negarte sin explicar nada"],
    explicacion: "Pedidos raros o en cantidad pueden ser mal uso. No decides sola: llamas al químico.",
  },
];

// ---- Azar sembrado (mismo examen si se vuelve a armar con la misma semilla) ----
export function azar(semilla: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < semilla.length; i++) h = Math.imul(h ^ semilla.charCodeAt(i), 16777619);
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const barajar = <T>(xs: T[], r: () => number): T[] => {
  const a = xs.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
};

// Principios activos de relleno para las opciones incorrectas (los más vendidos en botica de barrio).
const RELLENO_DCI = ["Paracetamol", "Ibuprofeno", "Naproxeno", "Amoxicilina", "Loratadina", "Omeprazol", "Diclofenaco", "Cetirizina", "Metformina", "Losartán", "Azitromicina", "Clorfenamina"];

const mayuscula = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);
const paMostrar = (pa: string) => pa.split(/\s*\+\s*/).map(mayuscula).join(" + ");

function opcionesCon(correcta: string, candidatos: string[], r: () => number): string[] {
  const vistos = new Set([normalizarNombre(correcta)]);
  const otras: string[] = [];
  for (const c of barajar(candidatos, r)) {
    const n = normalizarNombre(c);
    if (!n || vistos.has(n)) continue;
    vistos.add(n);
    otras.push(c);
    if (otras.length === 3) break;
  }
  return barajar([correcta, ...otras], r);
}

/** Elige `n` productos distintos si se puede (si hay menos, repite). */
function elegir<T>(xs: T[], n: number, r: () => number): T[] {
  if (xs.length === 0) return [];
  const b = barajar(xs, r);
  return Array.from({ length: n }, (_, i) => b[i % b.length]!);
}

export type ArmarExamenInput = {
  productos: ProductoExamen[]; // los que ella cargó hoy
  fichas: FichaExamen[]; // solo APROBADAS
  semilla: string;
};

/** Arma las 10 preguntas. null si hoy no cargó ningún producto (no hay examen). */
export function armarExamen({ productos, fichas, semilla }: ArmarExamenInput): PreguntaArmada[] | null {
  if (productos.length === 0) return null;
  const r = azar(semilla);
  const preguntas: Omit<PreguntaArmada, "orden">[] = [];
  const conPa = productos.filter((p) => claveDci(p.principio_activo));
  const marcas = conPa.filter((p) => tipoEfectivo(p.tipo, p.nombre, p.principio_activo) === "marca");
  const poolPa = [...new Set([...conPa.map((p) => paMostrar(p.principio_activo!)), ...fichas.map((f) => f.nombre_mostrar), ...RELLENO_DCI])];

  const generico = (p: ProductoExamen): Omit<PreguntaArmada, "orden"> => {
    const correcta = paMostrar(p.principio_activo!);
    return {
      tipo: "generico",
      enunciado: `¿Cuál es el principio activo de ${p.nombre}?`,
      opciones: opcionesCon(correcta, poolPa, r),
      correcta,
      explicacion: `${p.nombre} lleva ${correcta}.`,
      puntos: 1,
      producto_id: p.producto_id,
    };
  };

  // 1) Marca → genérico ×2 (de preferencia marcas: en un genérico el nombre ya dice la respuesta).
  const paraGenerico = marcas.length > 0 ? marcas : conPa;
  const genericos = elegir(paraGenerico, 2, r);

  // 2) ¿Pide receta? ×2, valen doble.
  for (const p of elegir(productos, 2, r)) {
    const si = "Sí, pide receta";
    const no = "No, es de venta libre";
    preguntas.push({
      tipo: "receta",
      enunciado: `¿${p.nombre} se vende con receta?`,
      opciones: [si, no],
      correcta: p.requiere_receta ? si : no,
      explicacion: p.requiere_receta ? `${p.nombre} pide receta: sin receta no se cobra.` : `${p.nombre} es de venta libre.`,
      puntos: 2,
      producto_id: p.producto_id,
    });
  }

  // 3) ¿Para qué sirve? ×2, de las fichas aprobadas; sin ficha suficiente, marca→genérico.
  const conFicha = productos.map((p) => ({ p, f: fichaDe(fichas, p.principio_activo) })).filter((x) => x.f) as { p: ProductoExamen; f: FichaExamen }[];
  const paraQue = fichas.length >= 4 ? elegir(conFicha, 2, r) : [];
  for (const { p, f } of paraQue) {
    preguntas.push({
      tipo: "para_que",
      enunciado: `¿Para qué sirve ${p.nombre}?`,
      opciones: opcionesCon(f.para_que_sirve, fichas.map((x) => x.para_que_sirve), r),
      correcta: f.para_que_sirve,
      explicacion: `${f.nombre_mostrar}: ${f.para_que_sirve}.`,
      puntos: 1,
      producto_id: p.producto_id,
    });
  }
  const faltanParaQue = 2 - paraQue.length;
  const extraGenericos = faltanParaQue > 0 ? elegir(paraGenerico, faltanParaQue, r) : [];
  if (paraGenerico.length > 0) {
    for (const p of [...genericos, ...extraGenericos]) preguntas.push(generico(p));
  } else {
    // Sin principio activo en ningún producto: esas preguntas pasan a receta (lo único que siempre se sabe).
    for (const p of elegir(productos, 2 + faltanParaQue, r)) {
      preguntas.push({
        tipo: "receta",
        enunciado: `¿${p.nombre} se vende con receta?`,
        opciones: ["Sí, pide receta", "No, es de venta libre"],
        correcta: p.requiere_receta ? "Sí, pide receta" : "No, es de venta libre",
        explicacion: p.requiere_receta ? `${p.nombre} pide receta.` : `${p.nombre} es de venta libre.`,
        puntos: 1,
        producto_id: p.producto_id,
      });
    }
  }

  // 4) Caso de mostrador ×2 (banco fijo).
  for (const c of barajar(CASOS_MOSTRADOR, r).slice(0, 2)) {
    preguntas.push({
      tipo: "caso",
      enunciado: c.enunciado,
      opciones: barajar([LLAMAR_QUIMICO, ...c.distractores], r),
      correcta: LLAMAR_QUIMICO,
      explicacion: c.explicacion,
      puntos: 1,
      producto_id: null,
    });
  }

  // 5) Práctica ×2: buscarlo en el Mostrador y decir precio y stock en 30 segundos.
  const conPrecio = productos.filter((p) => p.precio_dm !== null && p.stock !== null);
  for (const p of elegir(conPrecio.length > 0 ? conPrecio : productos, 2, r)) {
    preguntas.push({
      tipo: "practica",
      enunciado: `Búscalo en el Mostrador: ¿a cuánto se vende ${p.nombre} y cuántas unidades hay?`,
      opciones: null,
      correcta: JSON.stringify({ precio_dm: p.precio_dm, stock: p.stock }),
      explicacion: `${p.nombre}: S/ ${((p.precio_dm ?? 0) / 10000).toFixed(2)} · ${p.stock ?? 0} unidades.`,
      puntos: 1,
      producto_id: p.producto_id,
    });
  }

  // Orden: lo de memoria primero, la práctica al final (es la que pide salir a buscar).
  const peso: Record<TipoPregunta, number> = { generico: 0, receta: 1, para_que: 2, caso: 3, practica: 4 };
  return preguntas
    .sort((a, b) => peso[a.tipo] - peso[b.tipo])
    .map((p, i) => ({ ...p, orden: i + 1 }));
}

/** ¿Acertó? Opción: igual a la correcta. Práctica: precio (S/ con 2 decimales) y stock exactos. */
export function esAcierto(tipo: TipoPregunta, correcta: string, respuesta: string): boolean {
  if (tipo !== "practica") return respuesta === correcta;
  try {
    const c = JSON.parse(correcta) as { precio_dm: number | null; stock: number | null };
    const r = JSON.parse(respuesta) as { precio?: string; stock?: string };
    const precio = String(r.precio ?? "").trim().replace(/^s\/\.?\s*/i, "").replace(",", ".");
    if (!/^\d+(\.\d{1,2})?$/.test(precio)) return false;
    const stock = String(r.stock ?? "").trim();
    if (!/^\d+$/.test(stock)) return false;
    // Se compara en céntimos: es lo que se lee en la pantalla del Mostrador.
    const cent = Math.round(Number(precio) * 100);
    return c.precio_dm !== null && cent === Math.round(c.precio_dm / 100) && Number(stock) === c.stock;
  } catch {
    return false;
  }
}

/** Nota final: suma de puntos de las acertadas sobre el máximo de ese examen. */
export function calificar(preguntas: { puntos: number; acierto: boolean | null }[]): { puntaje: number; maximo: number; aprobado: boolean } {
  const maximo = preguntas.reduce((s, p) => s + p.puntos, 0);
  const puntaje = preguntas.reduce((s, p) => s + (p.acierto ? p.puntos : 0), 0);
  // Aprueba con 10 de 12; si un examen salió con otro máximo (pocas preguntas de receta), la misma proporción.
  return { puntaje, maximo, aprobado: maximo > 0 && puntaje * PUNTAJE_MAXIMO >= PUNTAJE_APRUEBA * maximo };
}
